import React from 'react';

import LicensePlateSolidIcon from '../../../../../../shared/icons/license-plate-solid-icon';
import ShieldCheckSolidIcon from '../../../../../../shared/icons/shield-check-solid-icon';
import Button from '../../../../../button/button';
import { InputError } from '../../../../../input/input-types';
import Select from '../../../../../select/select';
import Text from '../../../../../text/text';
import Tooltip from '../../../../../tooltip/tooltip';
import Typography from '../../../../../typography/typography';
import { EVENTS } from '../../../../fitment-constants';

import { useFeatureFlags } from '../../../../../../shared/hooks/hooks';

import { OptionValue } from '../../../../../../shared/types/select';
import { FEATURE_FLAGS } from '../../../../../../shared/constants/feature-flags';
import {
  CANADA_ISO_CODE,
  COUNTRIES,
  PROVINCES,
  STATES
} from '../../../../../../shared/constants/form';

import './fitment-license-lookup.scss';

interface SelectFieldConfig {
  error?: InputError;
  name: string;
  value: OptionValue;
}

interface StringFieldConfig {
  error?: InputError;
  name: string;
  value: string;
}

interface OnEventProps {
  name: string;
  payload?: unknown;
}

interface FitmentLicenseLookupProps {
  fields: {
    country: SelectFieldConfig;
    licensePlate: StringFieldConfig;
    state: SelectFieldConfig;
  };
  onEvent: ({ name, payload }: OnEventProps) => void;
  onFocusLicensePlateDropdown?: () => void;
  onSubmitLicensePlate: () => void;
  setCountry: (value: OptionValue) => void;
  setShowAddLicense: (boolean) => void;
  shouldAutofocusLicensePlate: boolean;
  showAddLicense: boolean;
  updateField: (name: string, value: OptionValue | string) => void;
}

function FitmentLicenseLookup(props: FitmentLicenseLookupProps) {
  const {
    fields,
    onEvent,
    onFocusLicensePlateDropdown,
    onSubmitLicensePlate,
    setCountry,
    setShowAddLicense,
    shouldAutofocusLicensePlate,
    showAddLicense,
    updateField
  } = props;

  const { country, licensePlate, state } = fields;

  const { featureFlags } = useFeatureFlags();

  const isFitmentRedesign = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_FITMENT_REDESIGN__]
  );

  return (
    <div styleName={isFitmentRedesign ? 'container-redesign' : 'container'}>
      {isFitmentRedesign && !showAddLicense ? (
        <>
          <div styleName="split-entry">
            <div styleName="split-entry-line" />
            <Typography styleName="or-redesign" variant="H6" weight="black">
              OR
            </Typography>
            <div styleName="split-entry-line" />
          </div>
          <Button
            onClick={() => setShowAddLicense(true)}
            styleName="show-license-search-btn"
          >
            <LicensePlateSolidIcon />
            <div styleName="license-search-text-container">
              <span styleName="show-license-search-text">Search by </span>
              <span
                style={{ fontWeight: 'bold' }}
                styleName="show-license-search-text"
              >
                License Plate
              </span>
            </div>
          </Button>
        </>
      ) : (
        <>
          {!isFitmentRedesign && (
            <>
              <Typography styleName="or" variant="H6" weight="black">
                OR
              </Typography>
              <Typography styleName="info-text" variant="H6" weight="black">
                Skip steps by entering your Vehicle license plate{' '}
                <Tooltip
                  content="Custom vanity plates or newer vehicles may not be found. If you're having trouble with the license plate lookup, please enter your vehicle's year, make and model to continue."
                  onClick={() =>
                    onEvent?.({
                      name: EVENTS.VEHICLE_FITMENT_LP_NEED_HELP
                    })
                  }
                  styleName="tooltip"
                />
              </Typography>
              <Typography styleName="disclaimer">
                <ShieldCheckSolidIcon styleName="icon" />
                This data is protected and will only be used to detect your
                vehicle model
              </Typography>
            </>
          )}
          <Text
            label={
              isFitmentRedesign
                ? 'Your License Plate'
                : 'Your vehicle license plate'
            }
            name={licensePlate.name}
            onBlur={e => updateField(licensePlate.name, e.target.value)}
            onChange={e => updateField(licensePlate.name, e.target.value)}
            onFocus={onFocusLicensePlateDropdown}
            shouldAutoFocus={shouldAutofocusLicensePlate}
            styleName={
              !licensePlate?.value && !isFitmentRedesign
                ? 'license-plate-select'
                : 'select'
            }
            value={licensePlate?.value?.toUpperCase()}
          />
          {(licensePlate?.value || isFitmentRedesign) && (
            <>
              <Select
                data-testid="fitment-license-lookup__country"
                isDisabled={isFitmentRedesign && !licensePlate?.value}
                isRequired
                isSearchable
                label={isFitmentRedesign ? 'Country' : ''}
                name={country.name}
                onChange={value => setCountry(value)}
                options={COUNTRIES}
                placeholder="Country"
                styleName="select"
                value={country.value}
              />
              <Select
                data-testid="fitment-license-lookup__state"
                error={state.error}
                isDisabled={
                  isFitmentRedesign ? !licensePlate?.value : !country.value
                }
                isRequired
                isSearchable
                label={isFitmentRedesign ? 'State' : ''}
                name={state.name}
                onChange={value => updateField(state.name, value)}
                options={
                  country?.value?.value === CANADA_ISO_CODE ? PROVINCES : STATES
                }
                placeholder="State"
                styleName="select"
                value={state.value}
              />
              <Button
                data-testid="fitment-license-lookup__submit-btn"
                isDisabled={
                  !(licensePlate.value && state.value && country.value)
                }
                isFullWidth={isFitmentRedesign}
                isPrimary
                onClick={onSubmitLicensePlate}
                styleName="submit-btn"
              >
                Submit
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default FitmentLicenseLookup;
